import { InputButton, type PlayerInput } from "@arcade-hockey/shared";
import { tuning } from "../state/tuning.js";
import type { MatchState, PlayerState } from "../state/types.js";
import { findTeamForSeat } from "./seat-control.js";

export function applyManualSwitch(state: MatchState, input: PlayerInput): void {
  if ((input.buttons & InputButton.Poke) === 0) return;

  const team = findTeamForSeat(state, input.seatId);
  if (team === null) return;

  if (state.tick - team.lastManualSwitchTick < tuning.switching.manualLockoutTicks) return;

  const seat = input.seatId;
  if (seat !== "P1" && seat !== "P2") return;
  const currentId = team.controlledByHuman[seat];
  if (currentId === null) return;

  const current = state.players.find((p) => p.id === currentId);
  if (current === undefined) return;
  if (current.hasPuck) return;

  const candidates = state.players.filter(
    (p) => p.teamId === team.teamId && p.id !== currentId && !p.benched,
  );
  if (candidates.length === 0) return;

  const target = pickNearestToPuck(candidates, state.puck.pos);
  if (target === undefined) return;

  team.controlledByHuman[seat] = target.id;
  team.lastManualSwitchTick = state.tick;
}

export function pickNearestToPuck(
  candidates: ReadonlyArray<PlayerState>,
  puckPos: { x: number; y: number },
): PlayerState | undefined {
  // Callers pass candidates derived from `state.players`, which is in
  // canonical sorted order (see createMatch). Tie-breaking by id is therefore
  // deterministic without re-sorting.
  let best: PlayerState | undefined;
  let bestDistSq = Number.POSITIVE_INFINITY;
  for (const p of candidates) {
    const dx = p.pos.x - puckPos.x;
    const dy = p.pos.y - puckPos.y;
    const distSq = dx * dx + dy * dy;
    if (distSq < bestDistSq) {
      bestDistSq = distSq;
      best = p;
    }
  }
  return best;
}
